import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {matchPath} from "react-router";
import {css, cx} from "@panda/css";
import lscache from "lscache";

import {WideContent} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";
import {useIsMounted} from "@pg-mono/hooks";
import {rpAppLink, rpAppPath} from "@pg-mono/rp-routes";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {rpCommonUrl, rpFeatureFlagVendorReportDownload} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {LeadModal} from "../../application/components/LeadModal";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource} from "../../application/utils/form/application_form_source";
import {RelatedArticlesSection} from "../../article/components/RelatedArticlesSection";
import {LocationLinks} from "../../atoms/LocationLinks";
import {FavouritesInfoModal} from "../../favorite/components/FavouritesInfoModal/FavouritesInfoModal";
import {ForYouModal} from "../../for_you/components/ForYouModal";
import {useHitUserSegmentLocationEntryOnce} from "../../hooks/use_hit_user_segment_location_entry_once";
import {usePrimaryClick} from "../../hooks/use_primary_click";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useOfferBuildings} from "../../hooks/useOfferBuildings";
import {HotlineModal} from "../../hotline/components/HotlineModal";
import {HOTLINE_CHATBOT_MODAL_TRIGGER_ID} from "../../hotline/constants/hotline_chatbot_modal_trigger";
import {backToUrl} from "../../navigation/actions/back_to_url_actions";
import {OfferAndPropertyBreadcrumbs} from "../../offer/atoms/OfferAndPropertyBreadcrumbs";
import {OfferLocationLinksWrapper} from "../../offer/atoms/OfferLocationLinksWrapper";
import {GalleryHolder} from "../../offer/components/gallery_holder/GalleryHolder";
import {LeadGeneratorSuccessInfo} from "../../offer/detail/components/lead_generator/LeadGeneratorSuccessInfo";
import {createOfferLink} from "../../offer/helpers/create_offer_link";
import {offerUrlBuilder} from "../../offer/helpers/friendly_offer_list/offer_url_builder";
import {createOfferListingAmplitudePendingDetailUrl} from "../../offer/list/helpers/offer_listing_amplitude_item_list_id";
import {useOfferListingAmplitudePendingDetailItemListContext} from "../../offer/list/hooks/use_offer_listing_amplitude_pending_detail_item_list_context";
import {hitGTMPropertyViewItem} from "../../offer/tracking/ecommerce_events/property/hit_gtm_property_view_item";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {OfferType} from "../../offer/types/OfferType";
import {getOfferDisplayStatus} from "../../offer/utils/get_offer_display_status";
import {PromotionModal} from "../../promotion/components/PromotionModal";
import {OfferPageAmplitudeItemListTrackingContextProvider} from "../../promotion/tracking/OfferPageAmplitudeItemListTrackingContext";
import {Country} from "../../region/types/Country";
import {useAppDispatch} from "../../store/hooks";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {propertyDetailPageview} from "../../tracking/algolytics_hits/property_detail_pageview";
import {trackAmplitudeOfferViewItem} from "../../tracking/amplitude/track_amplitude_offer_view_item";
import {gtmOfferGalleryEvents} from "../../tracking/google_tag_manager/gtm_event_offer_gallery_modal";
import {gtmProductDetailOnPropertyDetail} from "../../tracking/google_tag_manager/gtm_product_detail";
import {VendorDetailReportModal} from "../../vendor/components/detail/report/VendorDetailReportModal";
import {PropertyDetailForSale} from "../components/PropertyDetailForSale";
import {PropertyDetailHeaderHiddenPriceVariant} from "../components/PropertyDetailHeaderHiddenPriceVariant";
import {PropertyDetailLimited} from "../components/PropertyDetailLimited";
import {PropertyViewSectionTracker, PropertyViewSectionTrackingProvider} from "../components/PropertyViewSectionTracking";
import {SchemaPropertyDetail} from "../components/SchemaPropertyDetail";
import {useUserLastSeenProperties} from "../hooks/use_user_last_seen_properties";
import {PropertyDisplayType} from "../types/PropertyDisplayType";
import {isPropertyForSale as isPropertyDetailForSale} from "../utils/is_property_for_sale";

const HotlineChatBotModal = React.lazy(() => import("../../hotline/components/HotlineChatBotModal"));

export function PropertyDetailView() {
    const dispatch = useAppDispatch();
    const propertyDetail = useSelector((store: IRPStore) => store.property);
    const variables = useSelector((store: IRPStore) => store.variables.data);
    const otherPropertiesInOffer = useSelector((store: IRPStore) => store.property.otherPropertiesInOffer);
    const viewType = useSelector((store: IRPStore) => store.viewType.current);
    const isMounted = useIsMounted();

    const backToUrlAction = (url: string, text: string) => {
        dispatch(backToUrl(url, text));
    };

    const [isModalAndTabActive, _setIsModalAndTabActive] = useState<OfferModalActiveTab | null>(null);
    // Amplitude `location` of the gallery events for the currently open modal — set only by the entry points
    // that report an RP-specific screen, so the remaining ones keep the default of the tracking helper
    const [amplitudeGalleryLocation, setAmplitudeGalleryLocation] = useState<string | undefined>(undefined);
    const setIsModalAndTabActive = (tab: OfferModalActiveTab | null, source?: GTMEventApplicationSourceSection | null, amplitudeLocation?: string) => {
        if (tab && isModalAndTabActive === null) {
            // send this GTM event only when opening modal
            gtmOfferGalleryEvents.onOfferGalleryModalOpen(tab, viewType, source ?? null);
        }
        setAmplitudeGalleryLocation(tab ? amplitudeLocation : undefined);
        _setIsModalAndTabActive(tab);
    };

    const [isEnabledAlgolyticsHit, setEnableAlgolyticsHit] = useState(false);

    const {property, promotions, articles} = propertyDetail;

    const isGTMInitialized = useSelector((store: IRPStore) => store.googleTagManager);
    const {itemListContext: offerListingAmplitudeItemListContext, isResolved: isOfferListingAmplitudeItemListContextResolved} =
        useOfferListingAmplitudePendingDetailItemListContext({
            itemId: property?.offer.id,
            propertyId: property?.id
        });

    const responsiveLinkTarget = useResponsiveLinkTarget();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const {shouldHidePrices} = useShouldHidePrices();

    useEffect(() => {
        if (!!isModalAndTabActive) {
            setModalIsOpen(true);
        }
    }, [isModalAndTabActive]);

    useEffect(() => {
        if (!modalIsOpen) {
            setIsModalAndTabActive(null);
        }
    }, [modalIsOpen]);

    const {addLastSeenProperty} = useUserLastSeenProperties();
    useEffect(() => {
        if (property) {
            propertyDetailPageview(property, property.offer, property.offer.vendor);

            const isCountryRegionPoland = !property.offer.region.country || property.offer.region.country === Country.POLAND;
            const isFlatOrHouse = property.offer.type === OfferType.FLAT || property.offer.type === OfferType.HOUSE;
            if (isCountryRegionPoland && isFlatOrHouse) {
                addLastSeenProperty(property);
            }
        }

        const prevUrl = document.referrer;
        if (prevUrl !== "") {
            const url = new URL(prevUrl);
            const baseUrl = `${url.protocol}//${url.host}`;
            if (baseUrl === rpCommonUrl && backToPaths.some((path: string) => matchPath(url.pathname, {path: path, exact: true, strict: true}) !== null)) {
                backToUrlAction(prevUrl, "OSIEDLE");
                return;
            }
        }

        property && backToUrlAction(offerLink, `osiedle ${property.offer.name}`);
    }, [property]);

    useEffect(() => {
        if (property && isGTMInitialized && isOfferListingAmplitudeItemListContextResolved) {
            gtmProductDetailOnPropertyDetail(property);
            hitGTMPropertyViewItem({
                offer: property.offer,
                property
            });

            trackAmplitudeOfferViewItem({
                offer: property.offer,
                property: {
                    id: property.id,
                    number: property.number,
                    area: property.area,
                    rooms: property.rooms,
                    floor: property.floor,
                    price: property.price
                },
                itemListId: offerListingAmplitudeItemListContext?.itemListId,
                itemListName: offerListingAmplitudeItemListContext?.itemListName
            });
        }
    }, [
        property?.id,
        isGTMInitialized,
        isOfferListingAmplitudeItemListContextResolved,
        offerListingAmplitudeItemListContext?.itemListId,
        offerListingAmplitudeItemListContext?.itemListName
    ]);

    useHitUserSegmentLocationEntryOnce({
        enabled: !!property,
        regions: property ? [property.offer.region] : []
    });

    useEffect(() => {
        lscache.flushExpired();
    }, []);

    const backToPaths = [rpAppPath.offer.detail.base];

    if (property === null) {
        return null;
    }

    const {region_type_voivodeship, region_type_city, region_type_county, region_type_district, region_type_housing_estate, region_type_town} =
        property.offer.region.stats;

    const breadcrumbs = {
        region_type_voivodeship,
        region_type_city,
        region_type_county,
        region_type_district,
        region_type_housing_estate,
        region_type_town
    };
    const vendor = property.offer.vendor;
    const offer = property.offer;
    const offerLink = createOfferLink(property.offer);
    const handleOfferLinkClick = usePrimaryClick((event: React.MouseEvent<HTMLAnchorElement>) => {
        event.currentTarget.href = createOfferListingAmplitudePendingDetailUrl({
            url: event.currentTarget.href,
            itemId: offer.id,
            itemListContext: offerListingAmplitudeItemListContext
        });
    });
    const regionFriendlySlug = offerUrlBuilder({type: property.offer.type, region: property.offer.region.slug}).friendlySlug;
    const regionLink = rpAppLink.offer.listFriendly({friendlySlug: regionFriendlySlug});
    const isForSale = isPropertyDetailForSale(property);
    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";
    const isLimited = getOfferDisplayStatus(offer) === "limited";
    const isClassicView = (isForSale || isForSaleOverLimit) && !isLimited;
    const isPropertyForSale = property.for_sale;
    const isPropertyPublished = property.configuration.display_type === PropertyDisplayType.PUBLISHED;
    const showPriceAndContactButtons = isForSale;
    const hotlineWidgetTrackingEventCategory = region_type_city?.name || region_type_town?.name || property.offer.region.name;

    const {offerBuildings, fetchOfferBuildings} = useOfferBuildings();

    const onModalOpenChange = (tab: OfferModalActiveTab | null, source?: GTMEventApplicationSourceSection, amplitudeLocation?: string) => {
        setIsModalAndTabActive(tab, source, amplitudeLocation);

        offer?.configuration.show_building_models_on_map && fetchOfferBuildings(offer?.id);
    };

    const onPlanImageOpen = () => {
        if (isEnabledAlgolyticsHit) {
            showPropertyPlanView(ShowPropertyPlanView.PLAN_DEFAULT, property.offer, property.offer.vendor);
        }
    };

    const renderPropertyDetail = () => {
        if (isClassicView && isPropertyForSale && isPropertyPublished) {
            return (
                <PropertyDetailForSale
                    shouldMapBeRendered={!modalIsOpen}
                    changeModalTab={onModalOpenChange}
                    property={property}
                    offer={offer}
                    promotions={promotions}
                    variables={variables}
                    otherPropertiesInOffer={otherPropertiesInOffer}
                    showPriceAndContactButtons={showPriceAndContactButtons}
                    amplitudeItemListContext={offerListingAmplitudeItemListContext}
                />
            );
        }

        return (
            <PropertyDetailLimited
                shouldMapBeRendered={!modalIsOpen}
                changeModalTab={onModalOpenChange}
                property={property}
                offer={offer}
                variables={variables}
                relatedOffers={propertyDetail.relatedOffers}
            />
        );
    };

    const isAbroad = offer.region.country !== Country.POLAND;
    const newHeaderVariant = shouldHidePrices ? "blur" : "visible";

    const shouldRenderPropertyModal = isPropertyForSale && isPropertyPublished && !!property;
    const hasActivePromotions = promotions.length > 0;

    return (
        <OfferPageAmplitudeItemListTrackingContextProvider
            itemListContext={offerListingAmplitudeItemListContext}
            isResolved={isOfferListingAmplitudeItemListContextResolved}
            pageLocation="property_page"
        >
            <PropertyViewSectionTrackingProvider itemId={offer.id} propertyId={property.id}>
                <WideContent className={css({mt: 2})}>
                    <article data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.PROPERTY_BREADCRUMBS_SECTION}>
                        <OfferAndPropertyBreadcrumbs
                            country={offer.region.country.toString()}
                            type={offer.type}
                            breadcrumbs={breadcrumbs}
                            offer={{name: offer.name, slug: offer.slug, id: offer.id}}
                            groups={offer.groups}
                            vendor={{name: vendor.name, id: vendor.id, slug: vendor.slug}}
                            property={{id: property.id, number: property.number}}
                        />
                    </article>
                </WideContent>

                <WideContent className={headerWrapper}>
                    <PropertyDetailHeaderHiddenPriceVariant
                        property={property}
                        variant={newHeaderVariant}
                        promotions={promotions}
                        onLocationClick={() => setIsModalAndTabActive(OfferModalActiveTab.MAP, GTMEventApplicationSourceSection.HEADER)}
                        onOfferLinkClick={handleOfferLinkClick}
                    />
                </WideContent>

                {(!property.for_sale || isLimited || !isPropertyPublished) && (
                    <WideContent>
                        <div className={notAvailableWrapper}>
                            <Text as="p" variant="headline_6" mb={2}>
                                {!property.for_sale ? "Nieruchomość została sprzedana" : "Uwaga"}
                            </Text>

                            {!property.for_sale ? (
                                <Text variant="body_copy_2">
                                    Zobacz pozostałe mieszkania w inwestycji{" "}
                                    <strong>
                                        <a href={offerLink} className={cx("bt", underline)} target={responsiveLinkTarget} onMouseDown={handleOfferLinkClick}>
                                            {property.offer.name}
                                        </a>
                                    </strong>{" "}
                                    lub inne propozycje{" "}
                                    <strong>
                                        <a href={regionLink} className={underline}>
                                            {property.offer.region.name_declension_where}
                                        </a>
                                    </strong>
                                </Text>
                            ) : (
                                <Text variant="body_copy_2">
                                    Ta nieruchomość jest nieaktywna. Informacje na jej temat pomagają nam jednak tworzyć wiarygodną bazę informacji na temat
                                    lokalnego rynku nieruchomości statystycznych portalu
                                </Text>
                            )}
                        </div>
                    </WideContent>
                )}

                <section data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.PROPERTY_GALLERY_SECTION}>
                    <PropertyViewSectionTracker sectionName="gallery" />
                    <GalleryHolder
                        source={ApplicationSource.Property}
                        isOpen={modalIsOpen}
                        offer={property.offer}
                        offerBuildings={offerBuildings}
                        handleGalleryModalChange={(tab) => {
                            setEnableAlgolyticsHit(true);
                            setIsModalAndTabActive(tab, GTMEventApplicationSourceSection.IMAGE);
                        }}
                        modalState={isModalAndTabActive}
                        property={property}
                        amplitudeGalleryLocation={amplitudeGalleryLocation}
                        onPlanImageOpen={onPlanImageOpen}
                        onModalClose={() => {
                            setEnableAlgolyticsHit(false);
                            setModalIsOpen(false);
                        }}
                    />
                </section>

                {renderPropertyDetail()}

                {articles.length > 0 && (
                    <>
                        <PropertyViewSectionTracker sectionName="related_articles" />
                        <RelatedArticlesSection articles={articles} heading="Artykuły powiązane:" />
                    </>
                )}

                <SchemaPropertyDetail property={property} />

                {/* For now, we don't want to show abroad regions on offers listing view - so we don't want to show links to listings */}
                {property.offer.region.country === Country.POLAND && (
                    <OfferLocationLinksWrapper className={linksWrapperStyle}>
                        <LocationLinks
                            recommendedRegions={propertyDetail.recommendedRegions}
                            offerType={property.offer.type}
                            region={property.offer.region}
                            relatedOffers={propertyDetail.relatedOffers}
                        />
                    </OfferLocationLinksWrapper>
                )}

                <LeadGeneratorSuccessInfo isPropertyForSale={isClassicView && isPropertyForSale && isPropertyPublished} />

                {/** View Modals */}
                {isMounted && (
                    <HotlineChatBotModal hotlineModalTriggerId={HOTLINE_CHATBOT_MODAL_TRIGGER_ID} trackingEventCategory={hotlineWidgetTrackingEventCategory} />
                )}

                <FavouritesInfoModal offerId={property.offer.id} propertyId={property.id} />

                <ForYouModal />

                {shouldRenderPropertyModal &&
                    (hasActivePromotions ? (
                        <PromotionModal
                            vendorId={property.offer.vendor.id}
                            offerId={property.offer.id}
                            propertyId={property.id}
                            promotion={promotions[0]}
                            fallbackTitle={property.offer.name}
                        />
                    ) : (
                        <HotlineModal trackingEventCategory={hotlineWidgetTrackingEventCategory} />
                    ))}

                <LeadModal />

                {rpFeatureFlagVendorReportDownload && !isAbroad && <VendorDetailReportModal />}
            </PropertyViewSectionTrackingProvider>
        </OfferPageAmplitudeItemListTrackingContextProvider>
    );
}

const headerWrapper = css({
    my: 2,

    lg: {
        mt: 3,
        mb: 2
    }
});

const notAvailableWrapper = css({
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,
    p: 2,
    mb: 2
});

const linksWrapperStyle = css({
    p: 0,
    backgroundColor: "var(--colors-white)",

    md: {
        py: "6.4rem !important"
    }
});

const underline = css({
    textDecoration: "underline"
});
import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {matchPath} from "react-router";
import {css, cx} from "@panda/css";
import lscache from "lscache";

import {WideContent} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";
import {useIsMounted} from "@pg-mono/hooks";
import {rpAppLink, rpAppPath} from "@pg-mono/rp-routes";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {rpCommonUrl, rpFeatureFlagVendorReportDownload} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {LeadModal} from "../../application/components/LeadModal";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource} from "../../application/utils/form/application_form_source";
import {RelatedArticlesSection} from "../../article/components/RelatedArticlesSection";
import {LocationLinks} from "../../atoms/LocationLinks";
import {FavouritesInfoModal} from "../../favorite/components/FavouritesInfoModal/FavouritesInfoModal";
import {ForYouModal} from "../../for_you/components/ForYouModal";
import {useHitUserSegmentLocationEntryOnce} from "../../hooks/use_hit_user_segment_location_entry_once";
import {usePrimaryClick} from "../../hooks/use_primary_click";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useOfferBuildings} from "../../hooks/useOfferBuildings";
import {HotlineModal} from "../../hotline/components/HotlineModal";
import {HOTLINE_CHATBOT_MODAL_TRIGGER_ID} from "../../hotline/constants/hotline_chatbot_modal_trigger";
import {backToUrl} from "../../navigation/actions/back_to_url_actions";
import {OfferAndPropertyBreadcrumbs} from "../../offer/atoms/OfferAndPropertyBreadcrumbs";
import {OfferLocationLinksWrapper} from "../../offer/atoms/OfferLocationLinksWrapper";
import {GalleryHolder} from "../../offer/components/gallery_holder/GalleryHolder";
import {LeadGeneratorSuccessInfo} from "../../offer/detail/components/lead_generator/LeadGeneratorSuccessInfo";
import {createOfferLink} from "../../offer/helpers/create_offer_link";
import {offerUrlBuilder} from "../../offer/helpers/friendly_offer_list/offer_url_builder";
import {createOfferListingAmplitudePendingDetailUrl} from "../../offer/list/helpers/offer_listing_amplitude_item_list_id";
import {useOfferListingAmplitudePendingDetailItemListContext} from "../../offer/list/hooks/use_offer_listing_amplitude_pending_detail_item_list_context";
import {hitGTMPropertyViewItem} from "../../offer/tracking/ecommerce_events/property/hit_gtm_property_view_item";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {OfferType} from "../../offer/types/OfferType";
import {getOfferDisplayStatus} from "../../offer/utils/get_offer_display_status";
import {PromotionModal} from "../../promotion/components/PromotionModal";
import {OfferPageAmplitudeItemListTrackingContextProvider} from "../../promotion/tracking/OfferPageAmplitudeItemListTrackingContext";
import {Country} from "../../region/types/Country";
import {useAppDispatch} from "../../store/hooks";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {propertyDetailPageview} from "../../tracking/algolytics_hits/property_detail_pageview";
import {trackAmplitudeOfferViewItem} from "../../tracking/amplitude/track_amplitude_offer_view_item";
import {gtmOfferGalleryEvents} from "../../tracking/google_tag_manager/gtm_event_offer_gallery_modal";
import {gtmProductDetailOnPropertyDetail} from "../../tracking/google_tag_manager/gtm_product_detail";
import {VendorDetailReportModal} from "../../vendor/components/detail/report/VendorDetailReportModal";
import {PropertyDetailForSale} from "../components/PropertyDetailForSale";
import {PropertyDetailHeaderHiddenPriceVariant} from "../components/PropertyDetailHeaderHiddenPriceVariant";
import {PropertyDetailLimited} from "../components/PropertyDetailLimited";
import {PropertyViewSectionTracker, PropertyViewSectionTrackingProvider} from "../components/PropertyViewSectionTracking";
import {SchemaPropertyDetail} from "../components/SchemaPropertyDetail";
import {useUserLastSeenProperties} from "../hooks/use_user_last_seen_properties";
import {PropertyDisplayType} from "../types/PropertyDisplayType";
import {isPropertyForSale as isPropertyDetailForSale} from "../utils/is_property_for_sale";

const HotlineChatBotModal = React.lazy(() => import("../../hotline/components/HotlineChatBotModal"));

export function PropertyDetailView() {
    const dispatch = useAppDispatch();
    const propertyDetail = useSelector((store: IRPStore) => store.property);
    const variables = useSelector((store: IRPStore) => store.variables.data);
    const otherPropertiesInOffer = useSelector((store: IRPStore) => store.property.otherPropertiesInOffer);
    const viewType = useSelector((store: IRPStore) => store.viewType.current);
    const isMounted = useIsMounted();

    const backToUrlAction = (url: string, text: string) => {
        dispatch(backToUrl(url, text));
    };

    const [isModalAndTabActive, _setIsModalAndTabActive] = useState<OfferModalActiveTab | null>(null);
    // Amplitude `location` of the gallery events for the currently open modal — set only by the entry points
    // that report an RP-specific screen, so the remaining ones keep the default of the tracking helper
    const [amplitudeGalleryLocation, setAmplitudeGalleryLocation] = useState<string | undefined>(undefined);
    const setIsModalAndTabActive = (tab: OfferModalActiveTab | null, source?: GTMEventApplicationSourceSection | null, amplitudeLocation?: string) => {
        if (tab && isModalAndTabActive === null) {
            // send this GTM event only when opening modal
            gtmOfferGalleryEvents.onOfferGalleryModalOpen(tab, viewType, source ?? null);
        }
        setAmplitudeGalleryLocation(tab ? amplitudeLocation : undefined);
        _setIsModalAndTabActive(tab);
    };

    const [isEnabledAlgolyticsHit, setEnableAlgolyticsHit] = useState(false);

    const {property, promotions, articles} = propertyDetail;

    const isGTMInitialized = useSelector((store: IRPStore) => store.googleTagManager);
    const {itemListContext: offerListingAmplitudeItemListContext, isResolved: isOfferListingAmplitudeItemListContextResolved} =
        useOfferListingAmplitudePendingDetailItemListContext({
            itemId: property?.offer.id,
            propertyId: property?.id
        });

    const responsiveLinkTarget = useResponsiveLinkTarget();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const {shouldHidePrices} = useShouldHidePrices();

    useEffect(() => {
        if (!!isModalAndTabActive) {
            setModalIsOpen(true);
        }
    }, [isModalAndTabActive]);

    useEffect(() => {
        if (!modalIsOpen) {
            setIsModalAndTabActive(null);
        }
    }, [modalIsOpen]);

    const {addLastSeenProperty} = useUserLastSeenProperties();
    useEffect(() => {
        if (property) {
            propertyDetailPageview(property, property.offer, property.offer.vendor);

            const isCountryRegionPoland = !property.offer.region.country || property.offer.region.country === Country.POLAND;
            const isFlatOrHouse = property.offer.type === OfferType.FLAT || property.offer.type === OfferType.HOUSE;
            if (isCountryRegionPoland && isFlatOrHouse) {
                addLastSeenProperty(property);
            }
        }

        const prevUrl = document.referrer;
        if (prevUrl !== "") {
            const url = new URL(prevUrl);
            const baseUrl = `${url.protocol}//${url.host}`;
            if (baseUrl === rpCommonUrl && backToPaths.some((path: string) => matchPath(url.pathname, {path: path, exact: true, strict: true}) !== null)) {
                backToUrlAction(prevUrl, "OSIEDLE");
                return;
            }
        }

        property && backToUrlAction(offerLink, `osiedle ${property.offer.name}`);
    }, [property]);

    useEffect(() => {
        if (property && isGTMInitialized && isOfferListingAmplitudeItemListContextResolved) {
            gtmProductDetailOnPropertyDetail(property);
            hitGTMPropertyViewItem({
                offer: property.offer,
                property
            });

            trackAmplitudeOfferViewItem({
                offer: property.offer,
                property: {
                    id: property.id,
                    number: property.number,
                    area: property.area,
                    rooms: property.rooms,
                    floor: property.floor,
                    price: property.price
                },
                itemListId: offerListingAmplitudeItemListContext?.itemListId,
                itemListName: offerListingAmplitudeItemListContext?.itemListName
            });
        }
    }, [
        property?.id,
        isGTMInitialized,
        isOfferListingAmplitudeItemListContextResolved,
        offerListingAmplitudeItemListContext?.itemListId,
        offerListingAmplitudeItemListContext?.itemListName
    ]);

    useHitUserSegmentLocationEntryOnce({
        enabled: !!property,
        regions: property ? [property.offer.region] : []
    });

    useEffect(() => {
        lscache.flushExpired();
    }, []);

    const backToPaths = [rpAppPath.offer.detail.base];

    if (property === null) {
        return null;
    }

    const {region_type_voivodeship, region_type_city, region_type_county, region_type_district, region_type_housing_estate, region_type_town} =
        property.offer.region.stats;

    const breadcrumbs = {
        region_type_voivodeship,
        region_type_city,
        region_type_county,
        region_type_district,
        region_type_housing_estate,
        region_type_town
    };
    const vendor = property.offer.vendor;
    const offer = property.offer;
    const offerLink = createOfferLink(property.offer);
    const handleOfferLinkClick = usePrimaryClick((event: React.MouseEvent<HTMLAnchorElement>) => {
        event.currentTarget.href = createOfferListingAmplitudePendingDetailUrl({
            url: event.currentTarget.href,
            itemId: offer.id,
            itemListContext: offerListingAmplitudeItemListContext
        });
    });
    const regionFriendlySlug = offerUrlBuilder({type: property.offer.type, region: property.offer.region.slug}).friendlySlug;
    const regionLink = rpAppLink.offer.listFriendly({friendlySlug: regionFriendlySlug});
    const isForSale = isPropertyDetailForSale(property);
    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";
    const isLimited = getOfferDisplayStatus(offer) === "limited";
    const isClassicView = (isForSale || isForSaleOverLimit) && !isLimited;
    const isPropertyForSale = property.for_sale;
    const isPropertyPublished = property.configuration.display_type === PropertyDisplayType.PUBLISHED;
    const showPriceAndContactButtons = isForSale;
    const hotlineWidgetTrackingEventCategory = region_type_city?.name || region_type_town?.name || property.offer.region.name;

    const {offerBuildings, fetchOfferBuildings} = useOfferBuildings();

    const onModalOpenChange = (tab: OfferModalActiveTab | null, source?: GTMEventApplicationSourceSection, amplitudeLocation?: string) => {
        setIsModalAndTabActive(tab, source, amplitudeLocation);

        offer?.configuration.show_building_models_on_map && fetchOfferBuildings(offer?.id);
    };

    const onPlanImageOpen = () => {
        if (isEnabledAlgolyticsHit) {
            showPropertyPlanView(ShowPropertyPlanView.PLAN_DEFAULT, property.offer, property.offer.vendor);
        }
    };

    const renderPropertyDetail = () => {
        if (isClassicView && isPropertyForSale && isPropertyPublished) {
            return (
                <PropertyDetailForSale
                    shouldMapBeRendered={!modalIsOpen}
                    changeModalTab={onModalOpenChange}
                    property={property}
                    offer={offer}
                    promotions={promotions}
                    variables={variables}
                    otherPropertiesInOffer={otherPropertiesInOffer}
                    showPriceAndContactButtons={showPriceAndContactButtons}
                    amplitudeItemListContext={offerListingAmplitudeItemListContext}
                />
            );
        }

        return (
            <PropertyDetailLimited
                shouldMapBeRendered={!modalIsOpen}
                changeModalTab={onModalOpenChange}
                property={property}
                offer={offer}
                variables={variables}
                relatedOffers={propertyDetail.relatedOffers}
            />
        );
    };

    const isAbroad = offer.region.country !== Country.POLAND;
    const newHeaderVariant = shouldHidePrices ? "blur" : "visible";

    const shouldRenderPropertyModal = isPropertyForSale && isPropertyPublished && !!property;
    const hasActivePromotions = promotions.length > 0;

    return (
        <OfferPageAmplitudeItemListTrackingContextProvider
            itemListContext={offerListingAmplitudeItemListContext}
            isResolved={isOfferListingAmplitudeItemListContextResolved}
            pageLocation="property_page"
        >
            <PropertyViewSectionTrackingProvider itemId={offer.id} propertyId={property.id}>
                <WideContent className={css({mt: 2})}>
                    <article data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.PROPERTY_BREADCRUMBS_SECTION}>
                        <OfferAndPropertyBreadcrumbs
                            country={offer.region.country.toString()}
                            type={offer.type}
                            breadcrumbs={breadcrumbs}
                            offer={{name: offer.name, slug: offer.slug, id: offer.id}}
                            groups={offer.groups}
                            vendor={{name: vendor.name, id: vendor.id, slug: vendor.slug}}
                            property={{id: property.id, number: property.number}}
                        />
                    </article>
                </WideContent>

                <WideContent className={headerWrapper}>
                    <PropertyDetailHeaderHiddenPriceVariant
                        property={property}
                        variant={newHeaderVariant}
                        promotions={promotions}
                        onLocationClick={() => setIsModalAndTabActive(OfferModalActiveTab.MAP, GTMEventApplicationSourceSection.HEADER)}
                        onOfferLinkClick={handleOfferLinkClick}
                    />
                </WideContent>

                {(!property.for_sale || isLimited || !isPropertyPublished) && (
                    <WideContent>
                        <div className={notAvailableWrapper}>
                            <Text as="p" variant="headline_6" mb={2}>
                                {!property.for_sale ? "Nieruchomość została sprzedana" : "Uwaga"}
                            </Text>

                            {!property.for_sale ? (
                                <Text variant="body_copy_2">
                                    Zobacz pozostałe mieszkania w inwestycji{" "}
                                    <strong>
                                        <a href={offerLink} className={cx("bt", underline)} target={responsiveLinkTarget} onMouseDown={handleOfferLinkClick}>
                                            {property.offer.name}
                                        </a>
                                    </strong>{" "}
                                    lub inne propozycje{" "}
                                    <strong>
                                        <a href={regionLink} className={underline}>
                                            {property.offer.region.name_declension_where}
                                        </a>
                                    </strong>
                                </Text>
                            ) : (
                                <Text variant="body_copy_2">
                                    Ta nieruchomość jest nieaktywna. Informacje na jej temat pomagają nam jednak tworzyć wiarygodną bazę informacji na temat
                                    lokalnego rynku nieruchomości statystycznych portalu
                                </Text>
                            )}
                        </div>
                    </WideContent>
                )}

                <section data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.PROPERTY_GALLERY_SECTION}>
                    <PropertyViewSectionTracker sectionName="gallery" />
                    <GalleryHolder
                        source={ApplicationSource.Property}
                        isOpen={modalIsOpen}
                        offer={property.offer}
                        offerBuildings={offerBuildings}
                        handleGalleryModalChange={(tab) => {
                            setEnableAlgolyticsHit(true);
                            setIsModalAndTabActive(tab, GTMEventApplicationSourceSection.IMAGE);
                        }}
                        modalState={isModalAndTabActive}
                        property={property}
                        amplitudeGalleryLocation={amplitudeGalleryLocation}
                        onPlanImageOpen={onPlanImageOpen}
                        onModalClose={() => {
                            setEnableAlgolyticsHit(false);
                            setModalIsOpen(false);
                        }}
                    />
                </section>

                {renderPropertyDetail()}

                {articles.length > 0 && (
                    <>
                        <PropertyViewSectionTracker sectionName="related_articles" />
                        <RelatedArticlesSection articles={articles} heading="Artykuły powiązane:" />
                    </>
                )}

                <SchemaPropertyDetail property={property} />

                {/* For now, we don't want to show abroad regions on offers listing view - so we don't want to show links to listings */}
                {property.offer.region.country === Country.POLAND && (
                    <OfferLocationLinksWrapper className={linksWrapperStyle}>
                        <LocationLinks
                            recommendedRegions={propertyDetail.recommendedRegions}
                            offerType={property.offer.type}
                            region={property.offer.region}
                            relatedOffers={propertyDetail.relatedOffers}
                        />
                    </OfferLocationLinksWrapper>
                )}

                <LeadGeneratorSuccessInfo isPropertyForSale={isClassicView && isPropertyForSale && isPropertyPublished} />

                {/** View Modals */}
                {isMounted && (
                    <HotlineChatBotModal hotlineModalTriggerId={HOTLINE_CHATBOT_MODAL_TRIGGER_ID} trackingEventCategory={hotlineWidgetTrackingEventCategory} />
                )}

                <FavouritesInfoModal offerId={property.offer.id} propertyId={property.id} />

                <ForYouModal />

                {shouldRenderPropertyModal &&
                    (hasActivePromotions ? (
                        <PromotionModal
                            vendorId={property.offer.vendor.id}
                            offerId={property.offer.id}
                            propertyId={property.id}
                            promotion={promotions[0]}
                            fallbackTitle={property.offer.name}
                        />
                    ) : (
                        <HotlineModal trackingEventCategory={hotlineWidgetTrackingEventCategory} />
                    ))}

                <LeadModal />

                {rpFeatureFlagVendorReportDownload && !isAbroad && <VendorDetailReportModal />}
            </PropertyViewSectionTrackingProvider>
        </OfferPageAmplitudeItemListTrackingContextProvider>
    );
}

const headerWrapper = css({
    my: 2,

    lg: {
        mt: 3,
        mb: 2
    }
});

const notAvailableWrapper = css({
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,
    p: 2,
    mb: 2
});

const linksWrapperStyle = css({
    p: 0,
    backgroundColor: "var(--colors-white)",

    md: {
        py: "6.4rem !important"
    }
});

const underline = css({
    textDecoration: "underline"
});
